import { useContext, useEffect, useState } from 'react'

import { useDiagnoseArticleMutation } from '@/common/api/modules/articleDiagnosis'
import {
  ArticleDiagnosisImprovement,
  ArticleDiagnosisResult
} from '@/common/api/params/articleDiagnosis'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import AiLoading from '@/components/view/atoms/loading/AiLoading'
import LoadingObject, { LoadingObjectType } from '@/components/view/atoms/loading/LoadingObject'
import SeoScoreCircle from '@/components/view/atoms/score/SeoScoreCircle'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import AiLoadingArea from '@/components/view/layouts/loading/AiLoadingArea'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import PriorityBar from '@/components/view/molecules/bar/PriorityBar'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'
import ScoreBreakdownBars from '@/components/view/molecules/score/ScoreBreakdownBars'

import { ArticleDiagnosisModalContext } from './ArticleDiagnosisModalProvider'

// SEOスコア内訳のラベルマップ（AIプロンプトの breakdown キーと対応させる）
const BREAKDOWN_LABEL_MAP: Record<string, string> = {
  title_quality: 'タイトル品質',
  meta_description: 'メタ説明',
  content_structure: 'コンテンツ構造',
  internal_linking: '内部リンク',
  user_engagement: 'ユーザーエンゲージメント'
}

const effortLabel = (effort: string) =>
  effort === 'small' ? '小' : effort === 'large' ? '大' : '中'
const impactLabel = (impact: string) => (impact === 'high' ? '高' : impact === 'low' ? '低' : '中')

const effortBadgeType = (effort: string) =>
  effort === 'small'
    ? BadgeType.SUCCESS
    : effort === 'large'
      ? BadgeType.CRITICAL
      : BadgeType.CAUTION
const impactBadgeType = (impact: string) =>
  impact === 'high' ? BadgeType.CRITICAL : impact === 'low' ? BadgeType.DEFAULT : BadgeType.CAUTION

const SectionTitle = ({ children }: { children: string }) => (
  <Text size={14} weight='bold'>
    {children}
  </Text>
)

const ArticleDiagnosisResultBody = ({ diagnosis }: { diagnosis: ArticleDiagnosisResult }) => {
  if (diagnosis.error) {
    return (
      <Text size={14} color={TextColor.CRITICAL}>
        {diagnosis.error}
      </Text>
    )
  }

  return (
    <AreaContent>
      {diagnosis.seo_score && (
        <section className='flex flex-col gap-4 px-4'>
          <SectionTitle>SEOスコア</SectionTitle>
          <div className='flex flex-col items-center gap-6'>
            <SeoScoreCircle
              score={diagnosis.seo_score.total}
              summary={diagnosis.seo_score.summary}
            />
            {diagnosis.seo_score.breakdown && (
              <div className='flex-1'>
                <ScoreBreakdownBars
                  breakdown={diagnosis.seo_score.breakdown}
                  labelMap={BREAKDOWN_LABEL_MAP}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {diagnosis.improvements && diagnosis.improvements.length > 0 && (
        <section className='flex flex-col gap-2'>
          <SectionTitle>改善点リスト</SectionTitle>
          {diagnosis.improvements.map((item, idx) => (
            <PriorityBar
              key={idx}
              priority={item.priority}
              title={item.issue}
              badgeText={item.category}
              contentText={
                <>
                  {item.suggestion && <Text size={14}>{item.suggestion}</Text>}
                  {item.expected_impact && (
                    <Text size={12} color={TextColor.SUCCESS}>
                      期待効果: {item.expected_impact}
                    </Text>
                  )}
                </>
              }
            />
          ))}
        </section>
      )}
      {diagnosis.action_plan && diagnosis.action_plan.length > 0 && (
        <section className='flex flex-col gap-2'>
          <SectionTitle>アクションプラン</SectionTitle>
          {diagnosis.action_plan.map((step, idx) => (
            <div
              key={idx}
              className='border-ruri bg-ruri-secondary flex items-stretch gap-0 overflow-hidden rounded-lg border p-1'
            >
              <div className='bg-fill-brand flex size-10 shrink-0 items-center justify-center rounded'>
                <Text size={16} weight='bold' color={TextColor.ON_BG_FILL}>
                  {String(idx + 1).padStart(2, '0')}
                </Text>
              </div>
              <div className='flex flex-1 items-center gap-1.5 px-4 py-2'>
                <div className='flex flex-wrap items-center gap-2'>
                  <Badge size={12} badgeType={impactBadgeType(step.impact)}>
                    効果: {impactLabel(step.impact)}
                  </Badge>
                  <Badge size={12} badgeType={effortBadgeType(step.effort)}>
                    工数: {effortLabel(step.effort)}
                  </Badge>
                </div>
                <Text size={14} weight='bold'>
                  {step.action}
                </Text>
              </div>
            </div>
          ))}
        </section>
      )}
    </AreaContent>
  )
}

interface ContentProps {
  siteId: number
  articleProposalId: number
}

// モーダルが開くたびに再マウントし、AI診断を実行する
const ArticleDiagnosisModalContent = ({ siteId, articleProposalId }: ContentProps) => {
  const [diagnosis, setDiagnosis] = useState<ArticleDiagnosisResult | null>(null)
  const { trigger: diagnose, isMutating: diagnosing } = useDiagnoseArticleMutation({
    onSuccess: (res) => setDiagnosis(res.diagnosis),
    onError: () => setDiagnosis({ error: '診断に失敗しました' })
  })

  useEffect(() => {
    diagnose({ site_id: siteId, article_proposal_id: articleProposalId }).catch(() => undefined)
    // マウント時に一度だけ診断を実行する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ModalContent type={ModalContentType.NO_FRAME} maxKeep>
      {diagnosing ? (
        <AiLoadingArea type='modal'>
          <AiLoading description='AIが診断中です。しばらくお待ちください' />
        </AiLoadingArea>
      ) : diagnosis ? (
        <ArticleDiagnosisResultBody diagnosis={diagnosis} />
      ) : (
        <AreaContent>
          <Text size={14} color={TextColor.SECONDARY} align='center'>
            「AI診断する」を押すとAI診断結果が表示されます。
          </Text>
        </AreaContent>
      )}
    </ModalContent>
  )
}

const ArticleDiagnosisModal = () => {
  const { modal, showing, hide } = useContext(ArticleDiagnosisModalContext)

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text='AI診断結果' onClickClose={hide} />
      {modal.showing && (
        <ArticleDiagnosisModalContent
          siteId={modal.siteId}
          articleProposalId={modal.articleProposalId}
        />
      )}
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ArticleDiagnosisModal
